/**
 * New purchase order.
 *
 * The allocation preview is the reason this is a drawer and not a form post. Freight and
 * testing are entered once at PO level and land on the lines by units or by value, and the
 * per unit landed cost that produces is what the WAC will move to. Showing it before the PO
 * is created is the difference between ordering on price and ordering on cost.
 *
 * The expected date defaults to today plus the supplier's measured p80 lead time, with the
 * sample size shown, so nobody has to remember what "about two weeks" meant.
 */
import { useMemo, useState } from 'react';
import { Drawer, Button, MoneyText, Pill, SelectField, TextInput, useToast } from '@/components';
import { ApiProblem } from '@/lib/api';
import { useCreatePo, useSuppliers } from './queries';
import type { NewPoLineDraft } from './types';

export interface NewPoDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Set when the drawer was opened from a reorder suggestion. */
  prefill: {
    supplier_id: string | null;
    lines: NewPoLineDraft[];
  } | null;
}

const CENTS = 100;

/** Largest remainder allocation, the same rule the server applies. Preview only. */
function allocate(total: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (weights.length === 0) return [];
  if (sum <= 0 || total === 0) return weights.map(() => 0);
  const exact = weights.map((w) => (total * w) / sum);
  const floored = exact.map((v) => Math.floor(v));
  let remainder = total - floored.reduce((a, b) => a + b, 0);
  const order = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  for (const { i } of order) {
    if (remainder === 0) break;
    floored[i] = (floored[i] ?? 0) + 1;
    remainder -= 1;
  }
  return floored;
}

export function NewPoDrawer({ open, onClose, prefill }: NewPoDrawerProps) {
  const suppliers = useSuppliers();
  const createPo = useCreatePo();
  const toast = useToast();

  const [supplierId, setSupplierId] = useState(prefill?.supplier_id ?? '');
  const [lines, setLines] = useState<NewPoLineDraft[]>(prefill?.lines ?? []);
  const [freight, setFreight] = useState('0.00');
  const [testing, setTesting] = useState('0.00');
  const [basis, setBasis] = useState<'units' | 'value'>('units');
  const [expected, setExpected] = useState('');

  const supplierOptions = (suppliers.data?.items ?? []).map((s) => ({
    value: s.supplier_id,
    label: `${s.name}${s.lead_days_p80 !== null ? ` (p80 ${Math.round(s.lead_days_p80)}d, n=${s.lead_sample_n})` : ' (no measured lead time)'}`,
  }));
  const supplier = (suppliers.data?.items ?? []).find((s) => s.supplier_id === supplierId) ?? null;

  const freightCents = Math.round(Number(freight) * CENTS) || 0;
  const testingCents = Math.round(Number(testing) * CENTS) || 0;

  const preview = useMemo(() => {
    const weights =
      basis === 'value'
        ? lines.map((l) => l.qty_ordered * l.unit_cost_cents)
        : lines.map((l) => l.qty_ordered);
    const freightParts = allocate(freightCents, weights);
    const testingParts = allocate(testingCents, weights);
    return lines.map((line, index) => {
      const goods = line.qty_ordered * line.unit_cost_cents;
      const extras = (freightParts[index] ?? 0) + (testingParts[index] ?? 0);
      return {
        line,
        goods,
        freight: freightParts[index] ?? 0,
        testing: testingParts[index] ?? 0,
        total: goods + extras,
        landedUnit: line.qty_ordered > 0 ? Math.round((goods + extras) / line.qty_ordered) : 0,
      };
    });
  }, [basis, freightCents, lines, testingCents]);

  const goodsTotal = preview.reduce((sum, p) => sum + p.goods, 0);
  const landedTotal = preview.reduce((sum, p) => sum + p.total, 0);
  const allocatedTotal = landedTotal - goodsTotal;

  /** Default expected date from the supplier's measured p80, not from a typed guess. */
  const suggestedExpected = useMemo(() => {
    if (!supplier) return '';
    const days = supplier.lead_days_p80 ?? supplier.default_lead_days;
    const date = new Date(Date.now() + days * 86_400_000);
    return date.toISOString().slice(0, 10);
  }, [supplier]);

  const submit = async () => {
    if (supplierId === '' || lines.length === 0) return;
    try {
      const created = await createPo.mutateAsync({
        supplier_id: supplierId,
        currency: supplier?.currency ?? 'USD',
        expected_at: expected === '' ? suggestedExpected || null : expected,
        freight_allocation: basis,
        lines: lines.map((l) => ({
          product_id: l.product_id,
          sku: l.sku,
          qty_ordered: l.qty_ordered,
          unit_cost_cents: l.unit_cost_cents,
        })),
        costs: [
          ...(freightCents > 0
            ? [{ category: 'freight', amount_cents: freightCents, basis, po_line_id: null }]
            : []),
          ...(testingCents > 0
            ? [{ category: 'testing', amount_cents: testingCents, basis: 'units', po_line_id: null }]
            : []),
        ],
        notes: null,
      });
      toast.push({ tone: 'ok', message: `${created.po_number} created as a draft.` });
      onClose();
    } catch (error) {
      toast.push({
        tone: 'crit',
        message: error instanceof Error ? error.message : 'The purchase order was not created.',
        requestId: error instanceof ApiProblem ? error.shortRequestId : null,
      });
    }
  };

  const setQty = (index: number, value: string) => {
    const qty = Math.max(0, Math.round(Number(value)) || 0);
    setLines((current) => current.map((l, i) => (i === index ? { ...l, qty_ordered: qty } : l)));
  };

  const setCost = (index: number, value: string) => {
    const cents = Math.max(0, Math.round(Number(value) * CENTS) || 0);
    setLines((current) => current.map((l, i) => (i === index ? { ...l, unit_cost_cents: cents } : l)));
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New purchase order"
      id="new-po"
      actions={
        <Button
          onClick={() => void submit()}
          disabled={supplierId === '' || lines.length === 0 || createPo.isPending}
        >
          {createPo.isPending ? 'Creating' : 'Create draft'}
        </Button>
      }
    >
      <SelectField
        label="Supplier"
        required
        value={supplierId}
        options={[{ value: '', label: 'Choose a supplier' }, ...supplierOptions]}
        onChange={(event) => setSupplierId(event.target.value)}
      />

      {supplier ? (
        <p className="o-muted">
          {supplier.lead_insufficient_data
            ? `Insufficient data, using the ${supplier.default_lead_days} day default.`
            : `Measured lead time p50 ${Math.round(supplier.lead_days_median ?? 0)}d, p80 ${Math.round(supplier.lead_days_p80 ?? 0)}d over ${supplier.lead_sample_n} received orders.`}
        </p>
      ) : null}

      <TextInput
        label="Expected arrival"
        type="date"
        hint={suggestedExpected ? `Defaults to ${suggestedExpected}, from the supplier p80.` : undefined}
        value={expected === '' ? suggestedExpected : expected}
        onChange={(event) => setExpected(event.target.value)}
      />

      <h3 className="stock-po__heading">Lines</h3>
      {lines.length === 0 ? (
        <p className="o-muted">
          No lines yet. Open this from a reorder suggestion to prefill the SKU, quantity and
          last unit cost.
        </p>
      ) : (
        <table className="stock-po__lines">
          <caption className="o-visually-hidden">Purchase order lines</caption>
          <thead>
            <tr>
              <th scope="col">SKU</th>
              <th scope="col">Qty</th>
              <th scope="col">Unit cost</th>
              <th scope="col">Landed unit</th>
            </tr>
          </thead>
          <tbody>
            {preview.map((entry, index) => (
              <tr key={entry.line.product_id}>
                <td>
                  <span className="o-mono">{entry.line.sku ?? entry.line.name}</span>
                </td>
                <td>
                  <input
                    className="o-input stock-po__num"
                    inputMode="numeric"
                    aria-label={`Quantity for ${entry.line.sku ?? entry.line.name}`}
                    value={String(entry.line.qty_ordered)}
                    onChange={(event) => setQty(index, event.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="o-input stock-po__num"
                    inputMode="decimal"
                    aria-label={`Unit cost for ${entry.line.sku ?? entry.line.name}`}
                    value={(entry.line.unit_cost_cents / CENTS).toFixed(2)}
                    onChange={(event) => setCost(index, event.target.value)}
                  />
                </td>
                <td>
                  <MoneyText cents={entry.landedUnit} showCents />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 className="stock-po__heading">Order level costs</h3>
      <TextInput
        label="Freight"
        inputMode="decimal"
        value={freight}
        onChange={(event) => setFreight(event.target.value)}
      />
      <TextInput
        label="Testing"
        inputMode="decimal"
        hint="Split across the lines by units. A test that covers one batch only is entered on the batch."
        value={testing}
        onChange={(event) => setTesting(event.target.value)}
      />
      <SelectField
        label="Allocate freight by"
        value={basis}
        options={[
          { value: 'units', label: 'Units, the default' },
          { value: 'value', label: 'Goods value' },
        ]}
        onChange={(event) => setBasis(event.target.value === 'value' ? 'value' : 'units')}
      />

      <div className="stock-po__totals">
        <span>
          Goods <MoneyText cents={goodsTotal} />
        </span>
        <span>
          Allocated <MoneyText cents={allocatedTotal} />
        </span>
        <span>
          Landed total <MoneyText cents={landedTotal} />
        </span>
        {allocatedTotal === freightCents + testingCents ? (
          <Pill tone="ok">allocation balances</Pill>
        ) : (
          <Pill tone="crit">allocation does not balance</Pill>
        )}
      </div>
    </Drawer>
  );
}
